/// <reference types="office-js" />

import { marked } from 'marked';

// Outlook ignores <p> tag margins, so we use <br><br> for paragraphs
marked.use({
  breaks: true,
  renderer: {
    paragraph({ tokens }) {
      return this.parser.parseInline(tokens) + '<br><br>';
    }
  }
});

export const getSessionId = (): string | undefined => {
  if (typeof Office !== 'undefined' && Office.context && Office.context.mailbox && Office.context.mailbox.item) {
    const ewsId = Office.context.mailbox.item.conversationId;
    if (ewsId && typeof Office.context.mailbox.convertToRestId === 'function') {
      try {
        return Office.context.mailbox.convertToRestId(ewsId, Office.MailboxEnums.RestVersion.v2_0);
      } catch (error) {
        console.error("Error converting EWS ID to REST ID:", error);
      }
    }
    return ewsId;
  }
  return undefined;
};

export const getEmailContext = async (): Promise<string> => {
  return new Promise((resolve) => {
    if (typeof Office === 'undefined' || !Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
      resolve("");
      return;
    }

    const item = Office.context.mailbox.item;
    
    let subject = "";
    let body = "";
    let to = "";
    let cc = "";

    const finish = () => {
      const xmlParts =[];
      xmlParts.push(`<email_context>`);
      
      // 1. Group metadata together
      xmlParts.push(`  <metadata>`);
      if (subject) xmlParts.push(`    <subject>${subject}</subject>`);
      if (to) xmlParts.push(`    <to>${to}</to>`);
      if (cc) xmlParts.push(`    <cc>${cc}</cc>`);
      xmlParts.push(`  </metadata>`);
      
      if (body) {
        // 2. Clean up noisy formatting (removes zero-width chars and normalizes spacing)
        const cleanedBody = body
          .replace(/[\u200B-\u200D\uFEFF\u200E\u200F]/g, '') // Strip hidden signature/RTL characters
          .replace(/[ \t]+/g, ' ')                           // Collapse multiple spaces
          .replace(/(?:\r\n|\r|\n){3,}/g, '\n\n')            // Collapse 3+ newlines into a standard paragraph break
          .trim();

        // 3. Explicitly tell the model this contains both the draft AND the thread
        xmlParts.push(`  <current_draft_and_thread>\n${cleanedBody}\n  </current_draft_and_thread>`);
      }
      
      xmlParts.push(`</email_context>`);
      resolve(xmlParts.join('\n').trim());
    };

    let pendingCallbacks = 0;
    const checkDone = () => {
      pendingCallbacks--;
      if (pendingCallbacks <= 0) finish();
    };

    const getRecipientsStr = (recipients: Office.EmailAddressDetails[]) => {
      if (!recipients) return "";
      return recipients
        .map((r) => r.emailAddress ? `${r.displayName || ''} <${r.emailAddress}>`.trim() : "")
        .filter(Boolean)
        .join(", ");
    };

    // ... (Keep your existing Subject, To, CC, Body getAsync callbacks exactly as they are) ...
    // Subject
    if (item.subject && typeof item.subject.getAsync === 'function') {
      pendingCallbacks++;
      item.subject.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) subject = result.value;
        checkDone();
      });
    } else if (typeof item.subject === 'string') {
        subject = item.subject;
    }

    // To
    if (item.to && typeof item.to.getAsync === 'function') {
      pendingCallbacks++;
      item.to.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) to = getRecipientsStr(result.value);
        checkDone();
      });
    }

    // CC
    if (item.cc && typeof item.cc.getAsync === 'function') {
      pendingCallbacks++;
      item.cc.getAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) cc = getRecipientsStr(result.value);
        checkDone();
      });
    }

    // Body
    if (item.body && typeof item.body.getAsync === 'function') {
      pendingCallbacks++;
      item.body.getAsync(Office.CoercionType.Text, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) body = result.value;
        checkDone();
      });
    }

    if (pendingCallbacks === 0) finish();
  });
};

export interface DraftEmail {
  subject?: string;
  body?: string;
  to?: string | string[] | null;
  cc?: string | string[] | null;
}

export const injectDraftEmail = async (draft: DraftEmail): Promise<void> => {
  if (typeof Office === 'undefined' || !Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
    return;
  }

  const item = Office.context.mailbox.item;

  const toArray = (val: string | string[] | null | undefined): string[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    return val.split(',').map(s => s.trim()).filter(Boolean);
  };

  const getRecipientsStr = (recipients: Office.EmailAddressDetails[]) => {
    if (!recipients) return [];
    return recipients
      .map((r) => r.emailAddress ? r.emailAddress.trim() : "")
      .filter(Boolean);
  };

  const arraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const setB = new Set(b.map(s => s.toLowerCase()));
    return a.every(s => setB.has(s.toLowerCase()));
  };

  try {
    // Subject
    if (draft.subject && item.subject && typeof item.subject.getAsync === 'function' && typeof item.subject.setAsync === 'function') {
      const currentSubject = await new Promise<string>((resolve) => {
        item.subject.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : "");
        });
      });
      
      const normalizedDraftSubj = draft.subject.replace(/^(Re|Fwd):\s*/i, '').trim();
      const normalizedCurrSubj = currentSubject.replace(/^(Re|Fwd):\s*/i, '').trim();

      if (normalizedDraftSubj && normalizedCurrSubj !== normalizedDraftSubj && currentSubject !== draft.subject) {
        await new Promise<void>((resolve) => {
          item.subject.setAsync(draft.subject as string, () => resolve());
        });
      }
    }

    // To
    const toRecipients = toArray(draft.to);
    if (toRecipients.length > 0 && item.to && typeof item.to.getAsync === 'function' && typeof item.to.setAsync === 'function') {
      const currentTo = await new Promise<string[]>((resolve) => {
        item.to.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? getRecipientsStr(result.value) : []);
        });
      });
      
      if (!arraysEqual(toRecipients, currentTo)) {
        await new Promise<void>((resolve) => {
          item.to.setAsync(toRecipients, () => resolve());
        });
      }
    }

    // CC
    const ccRecipients = toArray(draft.cc);
    if (ccRecipients.length > 0 && item.cc && typeof item.cc.getAsync === 'function' && typeof item.cc.setAsync === 'function') {
      const currentCc = await new Promise<string[]>((resolve) => {
        item.cc.getAsync((result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded ? getRecipientsStr(result.value) : []);
        });
      });
      
      if (!arraysEqual(ccRecipients, currentCc)) {
        await new Promise<void>((resolve) => {
          item.cc.setAsync(ccRecipients, () => resolve());
        });
      }
    }

    // Body
    if (draft.body) {
      // Convert markdown to HTML
      const htmlBody = await marked.parse(draft.body as string);

      if (item.body && typeof item.body.setSelectedDataAsync === 'function') {
        await new Promise<void>((resolve) => {
          item.body.setSelectedDataAsync(
            htmlBody, 
            { coercionType: Office.CoercionType.Html }, 
            () => resolve()
          );
        });
      } else if (item.body && typeof item.body.setAsync === 'function') {
        await new Promise<void>((resolve) => {
          item.body.setAsync(
            htmlBody, 
            { coercionType: Office.CoercionType.Html }, 
            () => resolve()
          );
        });
      }
    }
  } catch (error) {
    console.error("Error injecting draft:", error);
  }
};

export interface EmailAttachment {
  name: string;
  contentType: string;
  base64Data: string;
}

export const getEmailAttachments = async (): Promise<EmailAttachment[]> => {
  return new Promise((resolve) => {
    if (typeof Office === 'undefined' || !Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
      resolve([]);
      return;
    }

    const item = Office.context.mailbox.item as Office.MessageCompose & Office.MessageRead;

    const processAttachments = (attachmentList: (Office.AttachmentDetails | Office.AttachmentDetailsCompose)[]) => {
      if (!attachmentList || !Array.isArray(attachmentList) || attachmentList.length === 0) {
        resolve([]);
        return;
      }

      const attachments: EmailAttachment[] = [];
      let pendingCallbacks = attachmentList.length;

      const checkDone = () => {
        pendingCallbacks--;
        if (pendingCallbacks <= 0) {
          resolve(attachments);
        }
      };

      attachmentList.forEach((attachment) => {
        // We only handle file attachments
        if (attachment.attachmentType === Office.MailboxEnums.AttachmentType.File) {
          if (typeof item.getAttachmentContentAsync !== 'function') {
            checkDone();
            return;
          }

          item.getAttachmentContentAsync(attachment.id, (result: Office.AsyncResult<Office.AttachmentContent>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const content = result.value;
              if (content.format === Office.MailboxEnums.AttachmentContentFormat.Base64) {
                attachments.push({
                  name: attachment.name,
                  contentType: (attachment as Office.AttachmentDetails).contentType || "application/octet-stream",
                  base64Data: content.content
                });
              }
            }
            checkDone();
          });
        } else {
          checkDone();
        }
      });
    };

    if (typeof item.getAttachmentsAsync === 'function') {
      // Compose mode
      item.getAttachmentsAsync((result: Office.AsyncResult<Office.AttachmentDetailsCompose[]>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          processAttachments(result.value);
        } else {
          processAttachments([]);
        }
      });
    } else if (item.attachments !== undefined) {
      // Read mode fallback
      processAttachments(item.attachments);
    } else {
      resolve([]);
    }
  });
};
